import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  FileText, Calendar, Image, Sparkles, Zap, Video, Send, Facebook, Instagram, Linkedin,
  Clock, CheckCircle, X, Loader2, CalendarDays, Play, RefreshCw, Search, MessageSquare,
  Share2, Download, Copy, FileDown, MessageCircle, Lightbulb, Check
} from "lucide-react";
import PlatformIcon from './PlatformIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Plattform-Farben für Badges (identisch zur Fassung im Monatsplan). ContentRow lebt
// hier, benutzte die Map aber ohne Definition → ReferenceError, sobald ein Beitrag
// Plattformen hatte.
const platformColors = {
  facebook: 'bg-blue-600 text-white',
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white',
  linkedin: 'bg-sky-700 text-white',
  tiktok: 'bg-gray-100 text-black',
  google_business: 'bg-orange-50 text-orange-600',
  // Threads: Meta-Corporate-Black, passend zur Threads-Brand-Identity.
  threads: 'bg-stone-900 text-white'
};

// Unbekannte/neue Plattform darf die Zeile nicht erneut sprengen.
const PLATFORM_BADGE_FALLBACK = 'bg-stone-200 text-stone-700';

const FacebookPreviewModal = ({ post, onClose }) => {
  if (!post) return null;
  
  const content = post.hook + '\n\n' + (post.core_content || post.text_content || '');
  const hashtags = post.hashtags?.slice(0, 5).join(' ') || '';
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Facebook className="text-blue-600" size={20} />
            <span className="font-bold">Facebook Vorschau</span>
            <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Simulation</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded">
            <X size={20} />
          </button>
        </div>
        
        {/* Facebook Post Preview */}
        <div className="p-4">
          {/* Post Header */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-fox-fire rounded-full flex items-center justify-center">
              <span className="text-white text-lg">🦊</span>
            </div>
            <div>
              <div className="font-bold text-sm">Förderfuchs</div>
              <div className="text-xs text-stone-500 flex items-center gap-1">
                <Clock size={12} />
                {post.scheduled_at ? format(new Date(post.scheduled_at), 'd. MMMM yyyy, HH:mm', { locale: de }) : 'Geplant'}
                <span className="mx-1">·</span>
                <span>🌐</span>
              </div>
            </div>
          </div>
          
          {/* Post Content */}
          <div className="mb-3">
            <p className="text-sm whitespace-pre-wrap">{content}</p>
            {hashtags && (
              <p className="text-sm text-blue-600 mt-2">{hashtags}</p>
            )}
          </div>
          
          {/* Image Placeholder */}
          <div className="bg-stone-100 rounded-lg aspect-video flex items-center justify-center mb-3">
            <div className="text-center text-stone-400">
              <Image size={40} className="mx-auto mb-2" />
              <p className="text-sm">Bild/Video Platzhalter</p>
            </div>
          </div>
          
          {/* Engagement Bar */}
          <div className="flex items-center justify-between text-stone-500 text-sm border-t pt-3">
            <button className="flex items-center gap-1 hover:text-blue-600">
              <span>👍</span> Gefällt mir
            </button>
            <button className="flex items-center gap-1 hover:text-blue-600">
              <MessageSquare size={16} /> Kommentieren
            </button>
            <button className="flex items-center gap-1 hover:text-blue-600">
              <Share2 size={16} /> Teilen
            </button>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 bg-stone-50 border-t">
          <div className="text-xs text-stone-500 text-center">
            🔵 Dies ist eine Simulation - Der Post wird nicht wirklich gepostet
          </div>
        </div>
      </div>
    </div>
  );
};

// LinkedIn Preview Modal
const LinkedInPreviewModal = ({ post, onClose }) => {
  if (!post) return null;
  
  const content = post.hook + '\n\n' + (post.core_content || post.text_content || '');
  const hashtags = post.hashtags?.slice(0, 5).join(' ') || '';
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Linkedin className="text-blue-700" size={20} />
            <span className="font-bold">LinkedIn Vorschau</span>
            <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Simulation</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded">
            <X size={20} />
          </button>
        </div>
        
        {/* LinkedIn Post Preview */}
        <div className="p-4">
          {/* Post Header */}
          <div className="flex items-start gap-3 mb-3">
            <div className="w-12 h-12 bg-fox-fire rounded-full flex items-center justify-center">
              <span className="text-white text-xl">🦊</span>
            </div>
            <div className="flex-1">
              <div className="font-bold text-sm">Förderfuchs</div>
              <div className="text-xs text-stone-500">AZAV-zertifizierter Bildungsträger</div>
              <div className="text-xs text-stone-400 flex items-center gap-1 mt-0.5">
                {post.scheduled_at ? format(new Date(post.scheduled_at), 'd. MMMM yyyy', { locale: de }) : 'Geplant'}
                <span>·</span>
                <span>🌐</span>
              </div>
            </div>
          </div>
          
          {/* Post Content */}
          <div className="mb-3">
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{content}</p>
            {hashtags && (
              <p className="text-sm text-blue-700 mt-3">{hashtags}</p>
            )}
          </div>
          
          {/* Image Placeholder */}
          <div className="bg-stone-100 rounded aspect-video flex items-center justify-center mb-3">
            <div className="text-center text-stone-400">
              <Image size={40} className="mx-auto mb-2" />
              <p className="text-sm">Bild/Video Platzhalter</p>
            </div>
          </div>
          
          {/* Engagement Stats */}
          <div className="flex items-center gap-2 text-xs text-stone-500 mb-2">
            <span>👍 💙 0</span>
            <span className="ml-auto">0 Kommentare · 0 Reposts</span>
          </div>
          
          {/* Engagement Bar */}
          <div className="flex items-center justify-around text-stone-600 text-sm border-t pt-3">
            <button className="flex items-center gap-1 px-3 py-2 hover:bg-stone-100 rounded">
              <span>👍</span> Gefällt mir
            </button>
            <button className="flex items-center gap-1 px-3 py-2 hover:bg-stone-100 rounded">
              <MessageSquare size={16} /> Kommentieren
            </button>
            <button className="flex items-center gap-1 px-3 py-2 hover:bg-stone-100 rounded">
              <RefreshCw size={16} /> Reposten
            </button>
            <button className="flex items-center gap-1 px-3 py-2 hover:bg-stone-100 rounded">
              <Send size={16} /> Senden
            </button>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 bg-stone-50 border-t">
          <div className="text-xs text-stone-500 text-center">
            🔵 Dies ist eine Simulation - Der Post wird nicht wirklich gepostet
          </div>
        </div>
      </div>
    </div>
  );
};

// Blog & WhatsApp Output Modal
const OutputFormatModal = ({ post, onClose }) => {
  const [blogContent, setBlogContent] = useState(null);
  const [whatsappContent, setWhatsappContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('blog');
  const [copied, setCopied] = useState(false);
  
  const generateBlog = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/output/blog/generate`, {
        post_id: post.id,
        title: post.hook?.split('.')[0] || 'Förderfuchs Artikel',
        hook: post.hook || '',
        content: post.core_content || post.text_content || '',
        hashtags: post.hashtags || [],
        topic: post.topic
      });
      setBlogContent(response.data);
    } catch (error) {
      toast.error('Fehler beim Generieren des Blog-Artikels');
    } finally {
      setLoading(false);
    }
  };
  
  const generateWhatsApp = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/output/whatsapp/generate`, {
        post_id: post.id,
        title: post.hook?.split('.')[0] || 'Förderfuchs',
        hook: post.hook || '',
        content: post.core_content || post.text_content || ''
      });
      setWhatsappContent(response.data);
    } catch (error) {
      toast.error('Fehler beim Generieren der WhatsApp-Nachricht');
    } finally {
      setLoading(false);
    }
  };
  
  const downloadBlog = () => {
    if (!blogContent) return;
    const blob = new Blob([blogContent.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = blogContent.filename || 'blog-artikel.md';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Blog-Artikel heruntergeladen!');
  };
  
  const copyWhatsApp = () => {
    if (!whatsappContent) return;
    navigator.clipboard.writeText(whatsappContent.content);
    setCopied(true);
    toast.success('WhatsApp-Text kopiert!');
    setTimeout(() => setCopied(false), 2000);
  };
  
  if (!post) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <FileDown className="text-purple-600" size={20} />
            <span className="font-bold">Blog & WhatsApp Export</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded">
            <X size={20} />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('blog')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              activeTab === 'blog' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-stone-500'
            }`}
          >
            <FileText size={16} /> Blog-Artikel
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              activeTab === 'whatsapp' ? 'border-b-2 border-green-600 text-green-600' : 'text-stone-500'
            }`}
          >
            <MessageCircle size={16} /> WhatsApp
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'blog' && (
            <div>
              {!blogContent ? (
                <div className="text-center py-8">
                  <FileText size={48} className="mx-auto mb-4 text-stone-300" />
                  <p className="text-stone-500 mb-4">Generiere einen vollständigen Blog-Artikel im Markdown-Format</p>
                  <button
                    onClick={generateBlog}
                    disabled={loading}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 mx-auto"
                  >
                    {loading ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
                    Blog-Artikel generieren
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-stone-500">{blogContent.length} Zeichen | Markdown</span>
                    <button
                      onClick={downloadBlog}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      <Download size={16} /> Als .md herunterladen
                    </button>
                  </div>
                  <pre className="bg-stone-900 text-stone-100 p-4 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap font-mono">
                    {blogContent.content}
                  </pre>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'whatsapp' && (
            <div>
              {!whatsappContent ? (
                <div className="text-center py-8">
                  <MessageCircle size={48} className="mx-auto mb-4 text-stone-300" />
                  <p className="text-stone-500 mb-4">Generiere eine kompakte WhatsApp-Nachricht (max. 800 Zeichen)</p>
                  <button
                    onClick={generateWhatsApp}
                    disabled={loading}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 mx-auto"
                  >
                    {loading ? <Loader2 className="animate-spin" size={16} /> : <MessageCircle size={16} />}
                    WhatsApp-Text generieren
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-sm ${whatsappContent.within_limit ? 'text-green-600' : 'text-amber-600'}`}>
                      {whatsappContent.length} / {whatsappContent.max_length} Zeichen
                    </span>
                    <button
                      onClick={copyWhatsApp}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                        copied ? 'bg-green-500 text-white' : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                      {copied ? 'Kopiert!' : 'In Zwischenablage kopieren'}
                    </button>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <pre className="whitespace-pre-wrap text-sm font-sans">
                      {whatsappContent.content}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 bg-stone-50 border-t">
          <div className="text-xs text-stone-500 text-center">
            🔵 Simulation - Inhalte werden generiert und geloggt
          </div>
        </div>
      </div>
    </div>
  );
};

// Trend-zu-Post Modal (für Kanal- und Datumauswahl)
const TrendToPostModal = ({ idea, topic, onClose, onSuccess }) => {
  const [channel, setChannel] = useState('instagram');
  const [scheduleLater, setScheduleLater] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  
  useEffect(() => {
    // Lade verfügbare Kanäle
    const fetchChannels = async () => {
      try {
        const response = await axios.get(`${API}/trend-to-post/channels`);
        setChannels(response.data.channels || []);
      } catch (error) {
        console.error('Error loading channels:', error);
      }
    };
    fetchChannels();
    
    // Default Datum: morgen
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setScheduledDate(tomorrow.toISOString().split('T')[0]);
  }, []);
  
  const handleCreatePost = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/trend-to-post/create`, {
        trend_idea: idea,
        trend_topic: topic,
        channel: channel,
        scheduled_date: scheduleLater ? null : scheduledDate,
        scheduled_time: scheduleLater ? null : scheduledTime || null
      });
      
      if (response.data.success) {
        toast.success(`⚡ Post für ${channel} erstellt!`);
        onSuccess(response.data.post);
        onClose();
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  if (!idea) return null;
  
  const selectedChannel = channels.find(c => c.id === channel);
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center gap-2">
            <Zap className="text-amber-500" size={20} />
            <span className="font-bold">In Monatsplan übernehmen</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/50 rounded">
            <X size={20} />
          </button>
        </div>
        
        {/* Idea Preview */}
        <div className="p-4 border-b bg-stone-50">
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs px-2 py-0.5 rounded ${
              idea.type === 'Tipp' ? 'bg-green-100 text-green-700' :
              idea.type === 'Frage' ? 'bg-blue-100 text-blue-700' :
              'bg-amber-100 text-amber-700'
            }`}>
              {idea.type}
            </span>
            <span className="font-medium text-sm">{idea.title}</span>
          </div>
          <p className="text-sm text-stone-600 line-clamp-2">"{idea.hook}"</p>
        </div>
        
        {/* Channel Selection */}
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Kanal auswählen
            </label>
            <div className="grid grid-cols-3 gap-2">
              {channels.map((ch) => (
                <button
                  key={ch.id}
                  onClick={() => setChannel(ch.id)}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    channel === ch.id 
                      ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200' 
                      : 'border-stone-200 hover:border-amber-300'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1">
                    {ch.id === 'instagram' && <Instagram size={20} className="text-pink-500" />}
                    {ch.id === 'facebook' && <Facebook size={20} className="text-blue-600" />}
                    {ch.id === 'linkedin' && <Linkedin size={20} className="text-blue-700" />}
                    {ch.id === 'tiktok' && <Play size={20} className="text-stone-800" />}
                    {ch.id === 'blog' && <FileText size={20} className="text-purple-600" />}
                    {ch.id === 'whatsapp' && <MessageCircle size={20} className="text-green-500" />}
                    <span className="text-xs font-medium">{ch.name}</span>
                  </div>
                </button>
              ))}
            </div>
            {selectedChannel && (
              <p className="text-xs text-stone-500 mt-2">
                {selectedChannel.tone} · {selectedChannel.hashtag_count} Hashtags
              </p>
            )}
          </div>
          
          {/* Date/Time Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-stone-700">
                Datum & Uhrzeit
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={scheduleLater}
                  onChange={(e) => setScheduleLater(e.target.checked)}
                  className="rounded border-stone-300"
                />
                Später planen (automatisch)
              </label>
            </div>
            
            {!scheduleLater && (
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
                />
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  placeholder="Automatisch"
                  className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
                />
              </div>
            )}
            
            {scheduleLater && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                ⏰ Datum und Uhrzeit werden automatisch gewählt (natürliche Verteilung)
              </p>
            )}
          </div>
          
          {/* What will be created */}
          <div className="p-3 bg-stone-50 rounded-lg">
            <h4 className="text-xs font-medium text-stone-500 mb-2">Was wird automatisch gesetzt:</h4>
            <ul className="text-xs text-stone-600 space-y-1">
              <li>✅ Förderfuchs Brand Voice</li>
              <li>✅ {selectedChannel?.hashtag_count || 12} kanal-optimierte Hashtags</li>
              <li>✅ Passender Call-to-Action</li>
              <li>✅ Badge "⚡ Aus Trend-Idee erstellt"</li>
            </ul>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t bg-stone-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg"
          >
            Abbrechen
          </button>
          <button
            onClick={handleCreatePost}
            disabled={loading}
            className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <CalendarDays size={16} />}
            Post erstellen
          </button>
        </div>
      </div>
    </div>
  );
};

// Perplexity Trend Modal
const TrendResearchModal = ({ onClose, onSelectIdea, onPostCreated }) => {
  const [topic, setTopic] = useState('');
  const [numIdeas, setNumIdeas] = useState(5);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState(null);
  // Modal für Trend-zu-Post
  const [selectedIdea, setSelectedIdea] = useState(null);
  
  useEffect(() => {
    // Lade Vorschläge und Status
    const fetchData = async () => {
      try {
        const [suggestionsRes, statusRes, logsRes] = await Promise.all([
          axios.get(`${API}/perplexity/suggestions`),
          axios.get(`${API}/perplexity/status`),
          axios.get(`${API}/perplexity/logs?limit=10`)
        ]);
        setSuggestions(suggestionsRes.data.suggestions || []);
        setStatus(statusRes.data.status);
        setLogs(logsRes.data.logs || []);
      } catch (error) {
        console.error('Error loading perplexity data:', error);
      }
    };
    fetchData();
  }, []);
  
  const handleResearch = async () => {
    if (!topic.trim()) {
      toast.error('Bitte gib ein Thema ein');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/perplexity/research`, {
        topic: topic,
        num_ideas: numIdeas
      });
      setResults(response.data);
      toast.success(`${response.data.ideas?.length || 0} Trend-Ideen generiert!`);
    } catch (error) {
      toast.error('Fehler bei der Trend-Recherche');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePostCreated = (post) => {
    if (onPostCreated) {
      onPostCreated(post);
    }
    setSelectedIdea(null);
  };
  
  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <Lightbulb className="text-amber-500" size={20} />
              <span className="font-bold">Perplexity Pro Trend-Recherche</span>
              {status && (
                <span className={`text-xs px-2 py-0.5 rounded ${
                  status.configured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {status.configured ? '🟢 Live' : '🔵 Simulation'}
                </span>
              )}
            </div>
            <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded">
              <X size={20} />
            </button>
          </div>
          
          {/* Search Input */}
          <div className="p-4 border-b bg-stone-50">
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="Thema eingeben (z.B. 'AVGS Gründungscoaching', 'Bewerbungscoaching 50+')"
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleResearch()}
                />
              </div>
              <select
                value={numIdeas}
                onChange={(e) => setNumIdeas(parseInt(e.target.value))}
                className="px-3 py-2 border border-stone-300 rounded-lg"
              >
                <option value={3}>3 Ideen</option>
                <option value={5}>5 Ideen</option>
                <option value={7}>7 Ideen</option>
              </select>
              <button
                onClick={handleResearch}
                disabled={loading || !topic.trim()}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
                Trends suchen
              </button>
            </div>
            
            {/* Quick Suggestions */}
            <div className="flex flex-wrap gap-2 mt-3">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setTopic(s.topic)}
                  className="text-xs px-3 py-1 bg-white border border-stone-200 rounded-full hover:border-amber-400 hover:bg-amber-50"
                >
                  {s.topic}
                </button>
              ))}
            </div>
          </div>
          
          {/* Results */}
          <div className="flex-1 overflow-y-auto p-4">
            {!results && !loading && (
              <div className="text-center py-8 text-stone-400">
                <Search size={48} className="mx-auto mb-4" />
                <p>Gib ein Thema ein und klicke auf "Trends suchen"</p>
                <p className="text-sm mt-2">Perplexity Pro generiert {numIdeas} konkrete Post-Ideen für dich</p>
              </div>
            )}
            
            {loading && (
              <div className="text-center py-8">
                <Loader2 size={48} className="mx-auto mb-4 animate-spin text-amber-500" />
                <p className="text-stone-600">Recherchiere Trends zu "{topic}"...</p>
              </div>
            )}
            
            {results && (
              <div>
                {/* Mode Indicator */}
                {results.mode === 'simulation' && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    <strong>🔵 Simulation:</strong> {results.hint}
                  </div>
                )}
                
                {/* Trending Keywords */}
                {results.recommended_hashtags?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-stone-600 mb-2">Empfohlene Hashtags:</h4>
                    <div className="flex flex-wrap gap-2">
                      {results.recommended_hashtags.map((tag, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Ideas */}
                <div className="space-y-4">
                  {results.ideas?.map((idea, index) => (
                    <div key={index} className="border border-stone-200 rounded-lg p-4 hover:border-amber-400 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              idea.type === 'Tipp' ? 'bg-green-100 text-green-700' :
                              idea.type === 'Frage' ? 'bg-blue-100 text-blue-700' :
                              idea.type === 'Mythos' ? 'bg-purple-100 text-purple-700' :
                              idea.type === 'Statistik' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {idea.type}
                            </span>
                            <h3 className="font-medium">{idea.title}</h3>
                          </div>
                          <p className="text-sm text-amber-700 font-medium mb-2">"{idea.hook}"</p>
                          <p className="text-sm text-stone-600">{idea.core_message}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {idea.hashtags?.map((tag, i) => (
                              <span key={i} className="text-xs text-blue-600">{tag}</span>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            data-testid={`add-to-plan-btn-${index}`}
                            onClick={() => setSelectedIdea(idea)}
                            className="px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-1 text-sm whitespace-nowrap"
                          >
                            <CalendarDays size={14} /> In Monatsplan
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Logs */}
          {logs.length > 0 && (
            <div className="border-t p-4 bg-stone-50 max-h-32 overflow-y-auto">
              <h4 className="text-xs font-medium text-stone-500 mb-2">Letzte Recherchen:</h4>
              <div className="space-y-1">
                {logs.slice(0, 5).map((log, i) => (
                  <div key={i} className="text-xs flex items-center justify-between text-stone-500">
                    <span className="truncate">{log.topic}</span>
                    <span>{log.ideas_count} Ideen · {log.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Trend-zu-Post Modal */}
      {selectedIdea && (
        <TrendToPostModal
          idea={selectedIdea}
          topic={topic || results?.topic || 'Trend'}
          onClose={() => setSelectedIdea(null)}
          onSuccess={handlePostCreated}
        />
      )}
    </>
  );
};

// Go-Live Checkliste & Herzlichen Glückwunsch Banner


const ContentRow = ({ item }) => (
  <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        item.content_type === 'video' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'
      }`}>
        {item.content_type === 'video' ? <Video size={18} /> : <FileText size={18} />}
      </div>
      <div>
        <p className="font-medium text-stone-900">{item.title}</p>
        <div className="flex items-center gap-2 mt-1">
          {item.platforms?.map(p => (
            <span key={p} className={`platform-badge ${platformColors[p] || PLATFORM_BADGE_FALLBACK}`}>
              {p}
            </span>
          ))}
        </div>
      </div>
    </div>
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
      item.status === 'published' ? 'status-published' : 
      item.status === 'scheduled' ? 'status-scheduled' : 'status-draft'
    }`}>
      {item.status === 'published' ? 'Veröffentlicht' : 
       item.status === 'scheduled' ? 'Geplant' : 'Entwurf'}
    </span>
  </div>
);

export { FacebookPreviewModal, LinkedInPreviewModal, OutputFormatModal, TrendToPostModal, TrendResearchModal, ContentRow };
